import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

export function useQuietMode() {
  const { user } = useAuth();
  const [quietMode, setQuietMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch quiet mode preference
  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    const fetchQuietMode = async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('quiet_mode')
        .eq('user_id', user.id)
        .maybeSingle();

      if (data) {
        setQuietMode(data.quiet_mode ?? false);
      }
      setIsLoading(false);
    };

    fetchQuietMode();
  }, [user]);

  // Toggle quiet mode
  const toggleQuietMode = useCallback(async (enabled: boolean) => {
    if (!user) return;

    setQuietMode(enabled);

    const { error } = await supabase
      .from('profiles')
      .update({ quiet_mode: enabled })
      .eq('user_id', user.id);

    if (error) {
      console.error('Error updating quiet mode:', error);
      setQuietMode(!enabled); // Revert on error
      toast.error('Failed to update preference');
    } else if (enabled) {
      toast.success('Quiet Mode enabled', {
        description: 'Progress indicators and prompts are now hidden.',
      });
    }
  }, [user]);

  return {
    quietMode,
    toggleQuietMode,
    isLoading,
  };
}
